<script setup lang="ts">
import LoyaltyCreateContainer from './LoyaltyCreateContainer.vue'
import LoyaltyCreateHelpBlock from './LoyaltyCreateHelpBlock.vue'
import CopyLink from '@/components/CopyLink.vue'
import axios from 'axios'

const { selectedPartner, email, password, goToStep } = useLoyalty()
const isLoading = ref(true)
const { stores } = useLocalStorage()
const { t } = useI18n()

const loadStores = async () => {
  const response = await axios.get(`partner-parameters/${selectedPartner.value?.id}/stores-from-partner`)
  if (response.data.data.length > 0) {
    goToStep(true)
  }

  isLoading.value = false
}

const steps = computed(() => {
  switch (selectedPartner.value.partner.slug) {
    case 'laddition':
      return 4
    case 'tillersystems':
      return 2
    case 'lightspeed':
      return 3
    case 'zelty':
      return 4
    case 'menlog':
      return 3
    case 'shopify':
      return 4
    default:
      return 0
  }
})

const authType = computed(() => {
  switch (selectedPartner.value.partner.slug) {
    case 'laddition':
      return 'webhooks'
    case 'lightspeed':
      return 'oauth'
    case 'tillersystems':
      return 'password'
    case 'zelty':
      return 'token'
    case 'menlog':
      return 'time-limited-token'
    case 'shopify':
      return 'customWebsiteUrl'
    default:
      return 'externalLink'
  }
})

watchEffect(async () => {
  if (authType.value === 'oauth') {
    await loadStores()
  }
  isLoading.value = false
})

const showForm = computed(() => authType.value !== 'oauth')

const title = computed(() => {
  switch (authType.value) {
    case 'webhooks':
      return t('pages.loyalty.create.yourPartnerCredential')
    case 'token':
      return t('pages.loyalty.create.yourApiKey')
    case 'customWebsiteUrl':
      return t('pages.loyalty.create.websiteLink')
    default:
      return t('pages.loyalty.create.yourCredentials', { name: selectedPartner.value?.partner?.name })
  }
})

const getStoreDetails = (storeId: number) => {
  return stores.value.find((store) => store.id === storeId)
}
</script>

<template>
  <LoyaltyCreateContainer content-type="full-content">
    <template v-if="!isLoading">
      <LoyaltyCreateHelpBlock :selected-partner="selectedPartner" />
      <div class="mt-10 flex w-full flex-col gap-2 px-10 xl:flex-row xl:justify-between">
        <div class="w-full max-w-md py-5">
          <h3 class="mb-5 text-left font-semibold">{{ t('pages.loyalty.create.nextSteps') }}</h3>
          <div class="flex flex-col gap-4">
            <div v-for="step in steps" :key="step" class="flex items-center">
              <BaseChip color="yellow" :text="step" />
              <p>{{ t(`pages.loyalty.create.partnerInstructions.${selectedPartner.partner.slug}.${step}`) }}</p>
            </div>
          </div>
        </div>
        <div
          v-if="showForm"
          class="flex max-h-[320px] w-full flex-col items-center justify-center overflow-x-auto rounded-lg bg-muted p-5 pb-10"
        >
          <div class="w-full">
            <div class="mb-8 flex items-center gap-2">
              <img
                class="aspect-square w-6 rounded-xs border bg-white object-contain p-1"
                :src="selectedPartner?.partner?.logo"
              />
              <h3 class="text-left font-semibold">
                {{ title }}
              </h3>
            </div>
            <template v-if="authType === 'webhooks'">
              <div v-for="(webhook, webhookIndex) in selectedPartner.webhooks" :key="webhookIndex" class="mt-4">
                <p v-if="selectedPartner.webhooks.length > 1" class="mb-2 truncate text-lg font-bold text-foreground">
                  {{ getStoreDetails(webhook.storeId).name }}
                </p>
                <CopyLink :link="webhook.hash" text="global.words.copy" :show-name="false" />
              </div>
            </template>
            <template v-else-if="authType === 'token'">
              <AppFormInput id="token" v-model="selectedPartner.apiToken" class="flex-1" />
            </template>
            <template v-else-if="authType === 'time-limited-token'">
              <AppFormInput id="merchantIdFromPartner" v-model="selectedPartner.merchantIdFromPartner" class="flex-1" />
            </template>
            <template v-else-if="authType === 'oauth' || authType === 'password'">
              <div class="mb-8">
                <AppFormInput id="email" v-model="email" :label="t('pages.loyalty.create.id')" name="email" />
              </div>
              <div>
                <AppFormInput
                  id="password"
                  v-model="password"
                  name="password"
                  type="password"
                  :label="t('pages.loyalty.create.password')"
                />
              </div>
            </template>
            <template v-else-if="authType === 'customWebsiteUrl'">
              <AppFormInput id="apiUrl" v-model="selectedPartner.apiUrl" class="flex-1" />
            </template>
          </div>
          <!-- End generic component when authentication type will be available -->
        </div>
      </div>
    </template>
  </LoyaltyCreateContainer>
</template>
